import { promotePersistedValuesToLocalStorage } from './persisted-storage.ts';

const localStorageKeys = {
  userAccessToken: 'coinraces-user-access-token',
  isDemoMode: 'coinraces-is-demo-mode',
};

type Key = keyof typeof localStorageKeys;

export type UserAccessTokenData = {
  tokenType: 'bearer';
  accessToken: string;
  expiresIn: number;
};

type Value<T extends Key> = T extends 'userAccessToken'
  ? UserAccessTokenData
  : T extends 'isDemoMode'
    ? boolean
    : never;

const memoryStore: Record<string, string> = {};

function resolveStorageKey(key: Key): string {
  return localStorageKeys[key];
}

function tryGetItem(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function trySetItem(store: Storage, key: string, value: string): boolean {
  try {
    store.setItem(key, value);
    return store.getItem(key) === value;
  } catch {
    return false;
  }
}

function tryRemoveItem(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // Ignore storage access errors in embedded / private browsing contexts.
  }
}

function readRawValue(key: Key): string | null {
  const storageKey = resolveStorageKey(key);

  const fromLocal = tryGetItem(localStorage, storageKey);
  if (fromLocal !== null) {
    return fromLocal;
  }

  const fromSession = tryGetItem(sessionStorage, storageKey);
  if (fromSession !== null) {
    return fromSession;
  }

  return memoryStore[storageKey] ?? null;
}

function writeRawValue(key: Key, serializedValue: string): boolean {
  const storageKey = resolveStorageKey(key);

  if (trySetItem(localStorage, storageKey, serializedValue)) {
    return true;
  }

  if (trySetItem(sessionStorage, storageKey, serializedValue)) {
    console.warn(
      '[coinraces] localStorage unavailable; persisted to sessionStorage:',
      storageKey
    );
    return true;
  }

  memoryStore[storageKey] = serializedValue;
  console.warn(
    '[coinraces] storage unavailable; using in-memory fallback:',
    storageKey
  );
  return true;
}

function removeRawValue(key: Key): void {
  const storageKey = resolveStorageKey(key);
  tryRemoveItem(localStorage, storageKey);
  tryRemoveItem(sessionStorage, storageKey);
  delete memoryStore[storageKey];
}

// Function overloads for better type inference
export function setLocalStorageValue(
  key: 'userAccessToken',
  value: UserAccessTokenData
): boolean;
export function setLocalStorageValue(key: 'isDemoMode', value: boolean): boolean;
export function setLocalStorageValue<T extends Key>(
  key: T,
  value: Value<T>
): boolean {
  try {
    const serializedValue = JSON.stringify(value);
    if (!writeRawValue(key, serializedValue)) {
      return false;
    }

    const readBack = readRawValue(key);
    return readBack === serializedValue;
  } catch (error) {
    console.error(`Serialisation error for key "${key}":`, error);
    return false;
  }
}

// Function overloads for better type inference
export function getLocalStorageValue(key: 'userAccessToken'): UserAccessTokenData | null;
export function getLocalStorageValue(key: 'isDemoMode'): boolean | null;
export function getLocalStorageValue<T extends Key>(key: T): Value<T> | null {
  const item = readRawValue(key);
  if (!item) return null;

  try {
    return JSON.parse(item) as Value<T>;
  } catch (error) {
    console.error(`Error parsing "${key}":`, error);
    return null;
  }
}

export const removeLocalStorageValue = (key: Key) => {
  removeRawValue(key);
};

export function initPersistedStorage(): Promise<void> {
  return promotePersistedValuesToLocalStorage();
}
